// LIHTC tab (widgetboss only) — the 26 CFR 1.42-10 utility allowance work queue.
//
// Properties arrive from the unlinked intake form and portfolio CSV uploads at
// /utility-allowances/lihtc. Nothing here computes a schedule: the schedules are
// produced by hand against tariffs sourced live for each town. This is the queue
// that work is driven from.
//
// The panel at the top is the one that matters operationally — tariff sourcing
// scales with TOWNS, not properties, so 70 properties across 6 new towns is six
// jobs. Everything below it is the property detail behind those jobs.
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, MapPin, ChevronDown, ChevronRight } from 'lucide-react';

const COMPLIANCE_API_URL =
  process.env.REACT_APP_COMPLIANCE_API ||
  'https://housing-compliance-service.onrender.com';
const COMPLIANCE_ADMIN_KEY =
  process.env.REACT_APP_COMPLIANCE_ADMIN_KEY || process.env.REACT_APP_ADMIN_API_KEY || '';

async function lihtcFetch(path, options = {}) {
  const url = new URL(`${COMPLIANCE_API_URL}/api/utility-allowance/lihtc${path}`, window.location.origin);
  if (COMPLIANCE_ADMIN_KEY) url.searchParams.set('key', COMPLIANCE_ADMIN_KEY);
  const res = await fetch(url.toString(), options);
  if (!res.ok) throw new Error(`LIHTC API error: ${res.status}`);
  return res.json();
}

const STATUS_LABELS = {
  new: 'New',
  rates_needed: 'Rates needed',
  in_progress: 'In progress',
  delivered: 'Delivered',
  blocked: 'Blocked — other method governs',
  declined: 'Declined',
};

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-700',
  rates_needed: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  blocked: 'bg-gray-200 text-gray-700',
  declined: 'bg-gray-100 text-gray-500',
};

const METHOD_LABELS = {
  husm: 'HUD Utility Schedule Model',
  pha: 'PHA allowance',
  utility_estimate: 'Utility company estimate',
  energy_model: 'Energy consumption model',
  agency: 'Agency estimate',
};

const ASSIST_LABELS = { rd: 'RD assisted', hud: 'HUD assisted', none: 'Neither' };
const PAYER_LABELS = { tenant: 'tenant', submetered: 'sub-metered', owner: 'owner' };
const SERVICE_LABELS = {
  heating: 'Heat', waterHeating: 'Hot water', cooking: 'Cooking',
  otherElectric: 'Other elec', ac: 'A/C', waterSewer: 'Water/sewer', trash: 'Trash',
};

const bedList = (b) => (Array.isArray(b) && b.length ? b.map((n) => (n === 0 ? 'Studio' : `${n}BR`)).join(', ') : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export default function LihtcTab() {
  const [requests, setRequests] = useState([]);
  const [towns, setTowns] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [expanded, setExpanded] = useState(() => new Set());
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await lihtcFetch('/admin/requests');
      setRequests(data.requests || []);
      setTowns(data.townsNeedingRates || []);
      setStatuses(data.statuses || []);
    } catch (err) {
      console.error('❌ Load LIHTC queue error:', err);
      setError('Failed to load the LIHTC queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    setSavingId(id);
    try {
      await lihtcFetch(`/admin/requests/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      setTowns((prev) => prev); // roll-up refreshes on next load
    } catch (err) {
      console.error('❌ LIHTC status update error:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const CLOSED = ['delivered', 'declined'];
  const visible = requests.filter((r) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'open') return !CLOSED.includes(r.status);
    return r.status === statusFilter;
  });

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading the LIHTC queue…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">LIHTC Utility Allowances</h2>
          <p className="text-sm text-gray-500">
            26 CFR 1.42-10 intake queue — {requests.length} {requests.length === 1 ? 'property' : 'properties'}.
            Schedules are produced by hand against live-sourced tariffs; nothing here runs automatically.
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{error}</div>}

      {towns.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-amber-700" />
            <span className="font-semibold text-amber-900 text-sm">
              Tariffs needed for {towns.length} {towns.length === 1 ? 'town' : 'towns'}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {towns.map((t) => (
              <span key={t.town} className="px-2 py-1 bg-white border border-amber-300 rounded text-xs text-amber-900">
                {t.town} <span className="text-amber-600">· {t.properties} {t.properties === 1 ? 'property' : 'properties'}</span>
              </span>
            ))}
          </div>
          <p className="text-xs text-amber-800 mt-2">
            Sourcing is per town, not per property — these are the jobs blocking everything above.
            Towns already in orgs.json are free.
          </p>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {[['open', 'Open'], ['all', 'All'], ...statuses.map((s) => [s, STATUS_LABELS[s] || s])].map(([v, l]) => (
          <button key={v} onClick={() => setStatusFilter(v)}
            className={`px-3 py-1 rounded text-xs font-medium ${statusFilter === v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {l}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm border border-gray-200 rounded">
          Nothing in the queue for this filter.
        </div>
      ) : (
        <div className="border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['', 'Property', 'Town', 'Bedrooms', 'Method', 'Status', 'Received'].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-2 py-2">
                      <button onClick={() => toggle(r.id)} className="text-gray-400 hover:text-gray-700">
                        {expanded.has(r.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      {r.property_name}
                      {r.source === 'csv' && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400">csv</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {r.town || '—'}
                      {!r.rates_on_file && <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px]">no rates</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{bedList(r.bedrooms)}</td>
                    <td className="px-3 py-2 text-gray-600">{METHOD_LABELS[r.method] || '—'}</td>
                    <td className="px-3 py-2">
                      <select
                        value={r.status}
                        disabled={savingId === r.id}
                        onChange={(e) => changeStatus(r.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded border-0 font-medium ${STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-600'}`}
                      >
                        {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{fmtDate(r.created_at)}</td>
                  </tr>

                  {expanded.has(r.id) && (
                    <tr className="bg-gray-50 border-t border-gray-100">
                      <td colSpan={7} className="px-6 py-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-600">
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Property</div>
                            <div>{r.address || 'no address given'}</div>
                            <div>{r.bin ? `BIN ${r.bin}` : 'no BIN'} · {r.units ? `${r.units} units` : 'units unknown'}</div>
                            <div>Placed in service {fmtDate(r.placed_in_service)}</div>
                            <div className="mt-1">{ASSIST_LABELS[r.assistance] || 'assistance not stated'}{r.has_voucher_holders ? ' · has voucher holders' : ''}</div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Who pays</div>
                            {Object.keys(SERVICE_LABELS).map((k) => (
                              <div key={k}>{SERVICE_LABELS[k]}: {PAYER_LABELS[r.services?.[k]] || '—'}</div>
                            ))}
                            {r.trash_monthly && <div className="mt-1">Trash charge ${r.trash_monthly}/mo</div>}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Equipment &amp; contact</div>
                            <div>Heat: {r.equipment?.heatingFuel || '—'} · Hot water: {r.equipment?.waterHeatFuel || '—'} · Cooking: {r.equipment?.cookingFuel || '—'}</div>
                            <div className="mt-1">{r.contact_name || 'no contact name'}{r.contact_org ? ` · ${r.contact_org}` : ''}</div>
                            {r.contact_email && <div>{r.contact_email}</div>}
                            {r.notes && <div className="mt-1 italic">“{r.notes}”</div>}
                          </div>
                        </div>
                        {r.status === 'blocked' && (
                          <div className="mt-2 text-xs text-gray-700 bg-white border border-gray-200 rounded px-3 py-2">
                            Flagged because the 1.42-10(b) hierarchy rules the HUD Utility Schedule Model out —
                            an RD or HUD assisted building, or a method other than HUSM elected. That allowance comes from the program, not from us.
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
